import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Sparkles, ListChecks, FileText, Wand2, CheckCircle2, Loader2,
  Save, ExternalLink, Trash2, AlertCircle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_OPTS = ["P0", "P1", "P2"];
const STATUS_LABEL = {
  queued: "Queued",
  outline_ready: "Outline ready",
  draft_ready: "Draft ready",
  promoted: "Promoted",
  archived: "Archived",
};

export default function AdminBlogTopicEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [runs, setRuns] = useState([]);
  const [providers, setProviders] = useState([]);
  const [defaultProvider, setDefaultProvider] = useState(null);

  const [providerId, setProviderId] = useState("");
  const [outline, setOutline] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingTopic, setSavingTopic] = useState(false);
  const [busy, setBusy] = useState(""); // "outline" | "full" | "expand" | "promote"

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const [topicRes, provRes] = await Promise.all([
        api.get(`/operations/blog/topics/${id}`),
        api.get(`/operations/blog/topics/llm/providers`),
      ]);
      const t = topicRes.data.topic;
      setTopic(t);
      setRuns(topicRes.data.recent_runs || []);
      setOutline(t.outline || "");
      setDraft(t.draft_html || "");
      setProviders(provRes.data.providers || []);
      setDefaultProvider(provRes.data.default);
      // Pick the first available model that matches the default; fall back to the first available overall.
      const dflt = provRes.data.default;
      const matched = (provRes.data.providers || []).find(
        (p) => p.available && p.provider === dflt.provider && p.model === dflt.model,
      );
      const fallback = (provRes.data.providers || []).find((p) => p.available);
      setProviderId(matched ? matched.id : (fallback ? fallback.id : ""));
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/admin/blog/topics");
    } finally {
      setLoading(false);
    }
  };

  const update = (k, v) => setTopic((t) => ({ ...t, [k]: v }));

  const saveTopic = async () => {
    setSavingTopic(true);
    try {
      const payload = {
        title: topic.title,
        target_keyword: topic.target_keyword || "",
        category: topic.category,
        target_word_count: topic.target_word_count,
        hero_variant: topic.hero_variant || null,
        intent: topic.intent || "",
        notes: topic.notes || "",
        priority: topic.priority,
      };
      await api.patch(`/operations/blog/topics/${id}`, payload);
      toast.success("Topic saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingTopic(false);
    }
  };

  const selectedProvider = providers.find((p) => p.id === providerId);
  const providerPayload = () => {
    if (!selectedProvider) return {};
    return { provider: selectedProvider.provider, model: selectedProvider.model };
  };

  const runOutline = async () => {
    setBusy("outline");
    try {
      const { data } = await api.post(`/operations/blog/topics/${id}/outline`, providerPayload());
      setOutline(data.text);
      toast.success(`Outline ready — ${data.input_tokens + data.output_tokens} tokens, $${(data.cost_usd || 0).toFixed(4)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  const runFullArticle = async () => {
    if (!window.confirm("Generate full article in one shot? This may take 30–90 seconds.")) return;
    setBusy("full");
    try {
      const { data } = await api.post(`/operations/blog/topics/${id}/generate`, providerPayload());
      setDraft(data.text);
      toast.success(`Draft ready — ${data.word_count} words, $${(data.cost_usd || 0).toFixed(4)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  const runExpand = async () => {
    if (!outline.trim()) { toast.error("Outline is empty — run 'Generate outline' first."); return; }
    setBusy("expand");
    try {
      const { data } = await api.post(`/operations/blog/topics/${id}/expand`, {
        ...providerPayload(),
        outline_text: outline,
      });
      setDraft(data.text);
      toast.success(`Draft ready from outline — ${data.word_count} words, $${(data.cost_usd || 0).toFixed(4)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  const promote = async () => {
    if (!draft.trim()) { toast.error("No draft to promote — generate one first."); return; }
    if (!window.confirm("Promote this draft to /admin/blog as a DRAFT blog post?\n\nIt will NOT be published automatically — you'll review and publish it manually.")) return;
    setBusy("promote");
    try {
      const { data } = await api.post(`/operations/blog/topics/${id}/promote`, {});
      toast.success("Promoted to Blog CMS as draft. Redirecting to the editor…");
      setTimeout(() => navigate(data.edit_url), 800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this topic from the queue? The article (if promoted) stays in Blog CMS.")) return;
    try {
      await api.delete(`/operations/blog/topics/${id}`);
      toast.success("Topic removed");
      navigate("/admin/blog/topics");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !topic) {
    return <div className="p-10 text-center text-[#6B7280]" data-testid="topic-editor-loading">Loading…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="admin-topic-editor">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <Link to="/admin/blog/topics" className="inline-flex items-center text-xs text-[#6B7280] hover:text-[#0066FF] mb-1.5">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to topic queue
          </Link>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A2540] max-w-3xl" data-testid="topic-title-heading">
            {topic.title}
          </h1>
          <div className="text-xs text-[#6B7280] mt-1 flex flex-wrap items-center gap-x-2">
            <span className="uppercase tracking-wider">{STATUS_LABEL[topic.status] || topic.status}</span>
            <span>·</span>
            <span>{topic.priority}</span>
            <span>·</span>
            <span>{topic.category}</span>
            {topic.last_generated_at && (
              <>
                <span>·</span>
                <span>Last run {formatDate(topic.last_generated_at)} via {topic.last_provider} / {topic.last_model}</span>
              </>
            )}
            {topic.promoted_slug && (
              <>
                <span>·</span>
                <Link to={`/admin/blog/${topic.promoted_slug}`} className="text-[#0066FF] hover:underline inline-flex items-center gap-1">
                  Open promoted draft <ExternalLink className="w-3 h-3" />
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 text-[#B91C1C] hover:bg-[#FEF2F2]" onClick={remove} data-testid="topic-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete topic
          </Button>
          <Button variant="outline" className="h-9" onClick={saveTopic} disabled={savingTopic} data-testid="topic-save-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {savingTopic ? "Saving…" : "Save topic"}
          </Button>
        </div>
      </div>

      {/* Safety rail banner */}
      <div className="mb-4 bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3 flex items-start gap-2 text-sm text-[#065F46]" data-testid="draft-only-banner">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>AI generations always create drafts.</strong> Nothing is ever auto-published. After "Promote", you'll review the post in the Blog CMS and publish manually.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Topic spec */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Topic spec</Label>
            <div>
              <Label className="text-[11px] text-[#6B7280]">Title</Label>
              <Input value={topic.title} onChange={(e) => update("title", e.target.value)} className="mt-1" data-testid="topic-input-title" />
            </div>
            <div>
              <Label className="text-[11px] text-[#6B7280]">Target keyword</Label>
              <Input value={topic.target_keyword || ""} onChange={(e) => update("target_keyword", e.target.value)} className="mt-1" data-testid="topic-input-keyword" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-[#6B7280]">Category</Label>
                <Input value={topic.category} onChange={(e) => update("category", e.target.value)} className="mt-1" data-testid="topic-input-category" />
              </div>
              <div>
                <Label className="text-[11px] text-[#6B7280]">Priority</Label>
                <Select value={topic.priority} onValueChange={(v) => update("priority", v)}>
                  <SelectTrigger className="mt-1 h-9" data-testid="topic-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-[#6B7280]">Word target</Label>
                <Input type="number" value={topic.target_word_count} onChange={(e) => update("target_word_count", Number(e.target.value) || 1200)} className="mt-1" data-testid="topic-input-wordcount" />
              </div>
              <div>
                <Label className="text-[11px] text-[#6B7280]">Hero variant (1-8)</Label>
                <Input type="number" min={1} max={8} value={topic.hero_variant || ""} onChange={(e) => update("hero_variant", e.target.value ? Number(e.target.value) : null)} className="mt-1" data-testid="topic-input-hero" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#6B7280]">Notes / angle</Label>
              <Textarea value={topic.notes || ""} onChange={(e) => update("notes", e.target.value)} rows={3} className="mt-1 text-sm" data-testid="topic-input-notes" />
            </div>
          </div>

          {/* Provider picker */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">AI provider</Label>
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger className="h-9" data-testid="topic-provider-select"><SelectValue placeholder="Choose a model" /></SelectTrigger>
              <SelectContent>
                {providers.filter((p) => p.available).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
                {providers.filter((p) => !p.available).map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled>{p.label} (add key in .env)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProvider && (
              <div className="text-[11px] text-[#6B7280]">
                <div><span className="font-medium text-[#0A2540]">Provider:</span> {selectedProvider.provider}</div>
                <div><span className="font-medium text-[#0A2540]">Model:</span> {selectedProvider.model}</div>
                {selectedProvider.recommended_for && <div className="italic mt-1">{selectedProvider.recommended_for}</div>}
              </div>
            )}
            {defaultProvider && (
              <div className="text-[10px] text-[#9CA3AF]">
                System default: <code>{defaultProvider.provider}/{defaultProvider.model}</code>
              </div>
            )}
          </div>

          {/* Recent runs */}
          {runs.length > 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280] block mb-2">Recent runs</Label>
              <ul className="space-y-1.5 text-[11px]">
                {runs.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 text-[#6B7280]" data-testid={`run-${r.id}`}>
                    <span className="truncate">{r.purpose.replace("topic_", "")} · {r.model}</span>
                    <span className="tabular-nums whitespace-nowrap">{(r.input_tokens + r.output_tokens).toLocaleString()}t · ${(r.cost_usd || 0).toFixed(4)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Generation workspace */}
        <div className="lg:col-span-2 space-y-4">
          {/* One-click mode */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-[#0066FF]" />
                  <h2 className="text-sm font-semibold text-[#0A2540]">One-click full article</h2>
                </div>
                <p className="text-xs text-[#6B7280] max-w-prose">
                  Generate a complete ~{topic.target_word_count}-word HTML article from the topic spec in a single LLM call.
                  Best for high-confidence topics. Output goes to "Draft" below for review.
                </p>
              </div>
              <Button
                className="h-9 bg-[#0066FF] hover:bg-[#0052CC]"
                onClick={runFullArticle}
                disabled={!providerId || !!busy}
                data-testid="generate-full-btn"
              >
                {busy === "full" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                {busy === "full" ? "Generating…" : "Generate full article"}
              </Button>
            </div>
          </div>

          {/* Outline-first mode */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ListChecks className="w-4 h-4 text-[#0A2540]" />
                  <h2 className="text-sm font-semibold text-[#0A2540]">Outline-first workflow</h2>
                </div>
                <p className="text-xs text-[#6B7280] max-w-prose">
                  Step 1: generate an outline (Markdown). Step 2: review/edit it. Step 3: expand the approved outline into a full article. Best for tricky or sensitive topics.
                </p>
              </div>
              <Button
                variant="outline"
                className="h-9"
                onClick={runOutline}
                disabled={!providerId || !!busy}
                data-testid="generate-outline-btn"
              >
                {busy === "outline" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5 mr-1.5" />}
                {busy === "outline" ? "Generating outline…" : (outline ? "Regenerate outline" : "Generate outline")}
              </Button>
            </div>
            <Textarea
              value={outline}
              onChange={(e) => setOutline(e.target.value)}
              rows={14}
              className="font-mono text-xs leading-relaxed"
              placeholder="Outline will appear here. You can edit it before expanding into the full article."
              data-testid="topic-outline-textarea"
            />
            <div className="mt-3 flex justify-end">
              <Button
                className="h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white"
                onClick={runExpand}
                disabled={!providerId || !outline.trim() || !!busy}
                data-testid="expand-from-outline-btn"
              >
                {busy === "expand" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                {busy === "expand" ? "Expanding…" : "Expand outline → full article"}
              </Button>
            </div>
          </div>

          {/* Draft */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-[#92400E]" />
                  <h2 className="text-sm font-semibold text-[#0A2540]">Generated draft (HTML body)</h2>
                  {draft && <span className="text-[11px] text-[#6B7280]">~{(draft.match(/\w+/g) || []).length} words</span>}
                </div>
                <p className="text-xs text-[#6B7280] max-w-prose">
                  Review/edit the HTML here. When ready, "Promote" creates a draft blog post in the Blog CMS — never auto-published.
                </p>
              </div>
              <Button
                className="h-9 bg-[#047857] hover:bg-[#065F46]"
                onClick={promote}
                disabled={!draft.trim() || !!busy}
                data-testid="promote-to-draft-btn"
              >
                {busy === "promote" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                {busy === "promote" ? "Promoting…" : "Promote to Blog CMS (draft)"}
              </Button>
            </div>
            {draft ? (
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={20}
                className="font-mono text-xs leading-relaxed"
                data-testid="topic-draft-textarea"
              />
            ) : (
              <div className="h-32 flex items-center justify-center text-xs text-[#9CA3AF] border-2 border-dashed border-[#E5E7EB] rounded">
                <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                No draft yet. Use one-click or outline-first above.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
